/* eslint-disable max-lines-per-function */
/* eslint-disable id-length */
/* eslint-disable functional/prefer-readonly-type */
import React, { useEffect, useState } from 'react'
import { Autocomplete, FormControl, TextField } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import { useHookstate, type State } from '@hookstate/core'
import { getDistricts } from '~/features/District/services'
import IDistrict from '~/features/District/interfaces/IDistrict'

interface FilterAutocompleteDistrictsWithCityProps {
  readonly onDistrictsChange: (value: number[]) => void
  readonly onCityChange?: (value: number[]) => void
  readonly districts: readonly number[]
  readonly cityId: any
  readonly refCityIds?: State<number[]>
  readonly error?: boolean
  readonly disabled?: boolean
  readonly limitedTags?: number
  readonly clearCityAndDistrict?: boolean
  readonly itemCityRemoved?: State<boolean>
  readonly itemDistrictRemoved?: State<boolean>
}

export default function FilterAutocompleteDistrictsWithCity({
  onDistrictsChange,
  onCityChange,
  districts,
  cityId,
  refCityIds,
  error,
  disabled,
  limitedTags = 5,
  clearCityAndDistrict,
  itemCityRemoved,
  itemDistrictRemoved,
}: FilterAutocompleteDistrictsWithCityProps) {
  const { translate } = useLang()
  const [options, setOptions] = useState<readonly IDistrict[]>([])
  const [optionsFilter, setOptionsFilter] = useState<readonly IDistrict[]>([])
  const [value, setValue] = useState<IDistrict[]>([])
  const loading = useHookstate(false)

  useEffect(() => {
    loading.set(true)
    getDistricts().then((districtsResponse) => {
      setOptions(districtsResponse)
      setOptionsFilter(districtsResponse)
      setValue([])
      loading.set(false)
    })
  }, [])

  useEffect(() => {
    if (!districts.length && cityId) {
      setValue([])
      onDistrictsChange([])
    }
  }, [districts?.length])

  useEffect(() => {
    if (cityId) {
      const filter = options.filter((element) =>
        cityId.includes(Number(element.city_id))
      )
      setOptionsFilter(filter.length ? filter : [])

      if (itemCityRemoved?.get()) {
        const valueFilter = value?.filter((element) =>
          cityId.includes(Number(element.city_id))
        )
        setValue(valueFilter)
        onDistrictsChange(valueFilter?.map((district) => Number(district.id)) ?? [])
        itemCityRemoved.set(false)
      }
    } else {
      setOptionsFilter(options)
      if (value?.length) {
        setValue([])
        onDistrictsChange([])
      }
    }
  }, [cityId])

  const verifyDisableButton = () => {
    if (
      cityId &&
      cityId.length > 0 &&
      (optionsFilter.length > 0 || districts.length > 0)
    ) {
      return false
    }
    return true
  }

  const removeDuplicity = (objDistrict: number[]) => {
    return Array.from(new Set(objDistrict))
  }

  const handleAddCityIdWithRefCities = ({
    district,
  }: {
    district: IDistrict[]
  }) => {
    if (district.length) {
      setValue(district)
    }

    if (!itemDistrictRemoved?.get()) {
      const filter = district.map((districtItem) =>
        Number(districtItem.city_id)
      )
      const allCities = filter.concat(cityId)
      const cityIdsForSending = removeDuplicity(allCities)

      refCityIds?.set(cityIdsForSending)
      onCityChange?.(cityIdsForSending)
      onDistrictsChange(district.map((district) => Number(district.id)) ?? [])
    }
  }

  const removeDistrict = ({ district }: { district: IDistrict[] }) => {
    itemDistrictRemoved?.set(true)

    if (
      cityId &&
      (typeof cityId === 'number' ||
        (Array.isArray(cityId) && cityId.length > 0))
    ) {
      onDistrictsChange(district.map((itemDistrict) => Number(itemDistrict.id)))

      const currentIds = district.length
        ? district.map((itemValue) => itemValue.id)
        : value?.map((itemValue) => itemValue.city_id)
      const currentItemRemoved = district.length
        ? value?.filter((itemValue) => !currentIds.includes(itemValue.id))
        : value
      const refIdsUpdated = currentItemRemoved
        ?.filter((itemRefId) =>
          refCityIds?.get().filter((idRemoved) => itemRefId.id == idRemoved)
        )
        .map((itemId) => itemId.city_id)
      const newRefs = refCityIds
        ?.get()
        .filter((itemRef) => !refIdsUpdated?.includes(itemRef))

      refCityIds?.set(newRefs ?? [])
      onCityChange?.(newRefs ?? [])
    }
  }

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        id="filter-districts"
        multiple
        limitTags={limitedTags}
        disabled={disabled || verifyDisableButton()}
        options={optionsFilter}
        value={value}
        filterSelectedOptions
        noOptionsText={translate('No options')}
        clearText={translate('Clear')}
        disableCloseOnSelect
        size="small"
        loading={loading.get()}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.name ?? ''}
        onChange={(_, districtsSelected) => {
          if (value && value.length > districtsSelected.length && cityId) {
            removeDistrict({ district: districtsSelected })
          } else {
            itemDistrictRemoved?.set(false)
          }

          if (
            cityId &&
            (typeof cityId === 'number' ||
              (Array.isArray(cityId) && cityId.length > 0)) &&
            !itemDistrictRemoved?.get()
          ) {
            handleAddCityIdWithRefCities({ district: districtsSelected })
          } else {
            onDistrictsChange(
              districtsSelected.map((district) => Number(district.id)) ?? []
            )
            setValue(districtsSelected)
          }

          if (!districtsSelected.length && cityId) {
            setValue([])
            onDistrictsChange([])
          }
        }}
        renderInput={(params) => (
          <TextField {...params} error={error} label={translate('Districts')} />
        )}
      />
    </FormControl>
  )
}
